import { PrintoutOrientation } from "@/utils/models";
import type { WeekStaffScheduleContentToggles, WeekStaffScheduleDetailToggles, WeekStaffScheduleExportPageSetup } from "./types";

/** Everything the export settings panel controls, held together so the preview can key off one object. */
export type WeekStaffScheduleExportSettings = WeekStaffScheduleContentToggles & WeekStaffScheduleDetailToggles & WeekStaffScheduleExportPageSetup;

/**
 * Exactly what the `user_settings.week_staff_schedule_export_settings` JSONB column holds.
 *
 * This type IS the wire shape — it is sent and received verbatim, with no mapping layer, which is why
 * the keys are camelCase (matching `custom_location_order.orderedLocationNames` and the rest of this
 * table's JSON payloads). The backend validates the same keys via `WeekScheduleExportSettings` in
 * `backend/models/authn.py`, with `extra="forbid"`: a key added here must reach the backend first or
 * every save will 422.
 *
 * Two panel concerns are deliberately absent:
 * - `includePreferences` — a hidden, forced-off feature (see `INCLUDE_PREFERENCES` below).
 * - the export date range — session-only, because it tracks whichever weeks the grid currently holds.
 */
export type PersistedWeekScheduleExportSettings = Omit<WeekStaffScheduleExportSettings, "includePreferences"> & {
	/**
	 * Preview-measured column widths in px, keyed "0".."6". `{}` — never null — means "no overrides".
	 *
	 * The empty object is load-bearing: the backend's update path dumps with `exclude_none=True`, which
	 * recurses into the document, so a null here would be stripped from the stored JSON and then fail
	 * the backend's own read validation.
	 */
	columnWidths: Record<string, number>;
};

/**
 * The document every user row starts from, and what the panel shows when the API returned no settings
 * at all (a non-head-nurse, whose settings query never runs).
 *
 * Mirrored by `DEFAULT_WEEK_SCHEDULE_EXPORT_SETTINGS` in `backend/models/authn.py` and by the
 * `ADD COLUMN` default in `database/Changes/.../2026_08_04_add_week_staff_schedule_export_settings.sql`.
 * `persistedSettings.spec.ts` pins this side of that agreement.
 */
export const DEFAULT_PERSISTED_EXPORT_SETTINGS: PersistedWeekScheduleExportSettings = {
	includeScheduled: true,
	includeActual: true,
	includeTimeOff: true,
	includeLocation: true,
	includeJobName: true,
	// A seven-column sheet needs the wide edge of the page.
	orientation: PrintoutOrientation.Landscape,
	fitToPage: true,
	// Fit the seven columns to one page across, then let the weeks flow down as many pages as needed.
	fitToWidth: 1,
	fitToHeight: 0,
	columnWidths: {},
};

/**
 * The single switch for the hidden "Prefers" content toggle.
 *
 * Off and not exposed in the panel: a "Prefers" entry only exists where an employee used the staff
 * app's self-scheduling want-to-work flow, so the control was dead weight for most employees. The
 * builder still supports it, and it is deliberately NOT persisted — flipping this constant and
 * uncommenting the row in `WeekStaffScheduleExportSettings.tsx` is the whole re-enable.
 */
const INCLUDE_PREFERENCES = false;

/** Widen a stored document into the panel's working shape. */
export const toPanelSettings = (persisted: PersistedWeekScheduleExportSettings): WeekStaffScheduleExportSettings => {
	const { columnWidths: _columnWidths, ...panel } = persisted;
	return { ...panel, includePreferences: INCLUDE_PREFERENCES };
};

/** Narrow the panel's working shape back to a storable document. */
export const toPersistedSettings = (panel: WeekStaffScheduleExportSettings, columnWidths: Record<string, number>): PersistedWeekScheduleExportSettings => {
	const { includePreferences: _includePreferences, ...persisted } = panel;
	return { ...persisted, columnWidths };
};
